import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Autocomplete from 'pages/components/Autocomplete';
import { getData, postData, clearData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import { entities } from 'pages/store/schema';
import { getProcessing, getValues, getErrors } from 'pages/store/selectors';
import { showErrors } from 'pages/utils/errors';

import formatOptions, { runOnChangeCallbackFunc } from './utils';

const DEFAULT_OPTIONS = {
  NONE: {
    LABEL: 'None',
    VALUE: 0,
  },
  ALL: {
    LABEL: 'All Vendors',
    VALUE: -1,
  },
};

/**
 * Functionality when the vendor dropdown is used with gui_defs
 *
 * @param {boolean} triggerViewFieldChanged - If the vendor change should trigger the field change to enable the save button
 * @param {Array} hideElementsOnChange - Array of DOM elements that need to be hidden when the vendor is changed
 */

const guiDefsVendorChange = (triggerViewFieldChanged, hideElementsOnChange) => {
  if (triggerViewFieldChanged) {
    window.viewFieldChanged('view');
  }

  if (hideElementsOnChange.length) {
    hideElementsOnChange.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.style.display = 'none';
      }
    });
  }
};

export class VendorsAutocomplete extends Component {
  static getDerivedStateFromProps(props) {
    const { vendors = [] } = props;

    if (vendors.length) {
      return {
        options: formatOptions(vendors),
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    const { initialValue, initialItems } = props;

    this.state = {
      value: initialValue === null ? '' : initialValue,
      options: initialItems,
      dirty: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleNewOptionClick = this.handleNewOptionClick.bind(this);
  }

  componentDidMount() {
    this.props.getData('/admin/vendors/all', response => {
      const vendors = response.data.vendors || [];
      const initialValue = this.props.initialValue;
      if (initialValue) {
        const isVendorUnarchived = vendors.some(({ vendorId }) => vendorId === initialValue);
        if (!isVendorUnarchived) {
          this.props.getArchivedVendorInUse(`/admin/vendors/${initialValue}`);
        }
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { loadingErrors, savingErrors } = this.props;

    if (!prevProps.loadingErrors && loadingErrors) {
      showErrors(loadingErrors, true);
    }

    if (!prevProps.savingErrors && savingErrors) {
      showErrors(savingErrors, true);
    }
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  getOptions() {
    // get the available list for vendor dropdown options from props
    let { options } = this.state;
    const { hasNone, hasAll } = this.props;
    if (hasNone) {
      options = [
        { value: DEFAULT_OPTIONS.NONE.VALUE, label: DEFAULT_OPTIONS.NONE.LABEL, translate: true },
      ].concat(options);
    }
    if (hasAll) {
      options = [
        { value: DEFAULT_OPTIONS.ALL.VALUE, label: DEFAULT_OPTIONS.ALL.LABEL, translate: true },
      ].concat(options);
    }
    return options;
  }

  getSelectValue() {
    const { value } = this.state;
    const { hasNone, isClearable } = this.props;

    if (value === '' && hasNone && !isClearable) {
      return DEFAULT_OPTIONS.NONE.VALUE;
    }

    return value;
  }

  createVendor(value) {
    this.props.postData('/admin/vendors', { vendor: { name: value } }, response => {
      const { data: { vendors: { vendorID } = {} } = {} } = response;
      this.handleChange(vendorID);
    });
  }

  handleNewOptionClick(newOption = {}) {
    this.createVendor(newOption.label);
  }

  handleChange(value) {
    const {
      hideElementsOnChange,
      isDirtyOnChange,
      triggerViewFieldChanged,
      onChangeCallback,
    } = this.props;
    const { dirty } = this.state;

    if (!dirty && isDirtyOnChange) {
      guiDefsVendorChange(triggerViewFieldChanged, hideElementsOnChange);
    }

    this.setState({
      value: value === null ? '' : value,
      dirty: true,
    });

    if (onChangeCallback) {
      runOnChangeCallbackFunc(onChangeCallback, value);
    }
  }

  render() {
    const { isSaving, loadingErrors, savingErrors } = this.props;

    const {
      name,
      placeholder,
      disabled,
      isCreatable,
      isClearable,
      inputProps,
      tabIndex,
    } = this.props;
    return (
      <Autocomplete
        name={name}
        options={this.getOptions()}
        value={this.getSelectValue()}
        placeholder={placeholder}
        disabled={disabled}
        isLoading={isSaving}
        isError={!!loadingErrors || !!savingErrors}
        inputProps={inputProps}
        creatable={isCreatable}
        clearable={isClearable}
        tabIndex={tabIndex}
        onChange={this.handleChange}
        onNewOptionClick={this.handleNewOptionClick}
      />
    );
  }
}

VendorsAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  inputProps: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    maxLength: PropTypes.number,
  }),
  tabIndex: PropTypes.string,
  isCreatable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDirtyOnChange: PropTypes.bool,
  hasNone: PropTypes.bool,
  hasAll: PropTypes.bool,
  triggerViewFieldChanged: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  hideElementsOnChange: PropTypes.arrayOf(PropTypes.string),
  onChangeCallback: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

VendorsAutocomplete.defaultProps = {
  initialValue: '',
  initialItems: [],
  inputProps: {},
  tabIndex: '0',
  isCreatable: true,
  isClearable: true,
  isDirtyOnChange: true,
  triggerViewFieldChanged: true,
  placeholder: 'Select or Enter a Vendor',
  disabled: false,
  hideElementsOnChange: [],
  onChangeCallback: '',
};

const mapStateToProps = state => ({
  vendors: getValues(entities.vendor)(state),
  isLoading: getProcessing({
    entity: entities.vendor,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  isSaving: getProcessing({ entity: entities.vendor, list: false, actionType: actionTypes.SAVING })(
    state
  ),
  loadingErrors: getErrors({
    entity: entities.vendor,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  savingErrors: getErrors({ entity: entities.vendor, list: false, actionType: actionTypes.SAVING })(
    state
  ),
});

const mapDispatchToProps = dispatch => ({
  getData: (url, onSuccess) =>
    dispatch(getData({ url, entity: entities.vendor, list: true, onSuccess })),
  getArchivedVendorInUse: url => dispatch(getData({ url, entity: entities.vendor })),
  postData: (url, data, onSuccess) =>
    dispatch(
      postData({
        url,
        payload: data,
        entity: entities.vendor,
        list: false,
        onSuccess,
      })
    ),
  clearData: () => dispatch(clearData(entities.vendor)),
});

const ConnectedVendorsAutocomplete = connect(
  mapStateToProps,
  mapDispatchToProps
)(VendorsAutocomplete);
export default ConnectedVendorsAutocomplete;
